/**
 * Route prefetch — automatically discovers page modules via Vite's
 * import.meta.glob so the map stays in sync when pages are added or removed.
 *
 * How it works:
 *   1. import.meta.glob collects every *Page.tsx / *Page.ts file under
 *      src/pages/ and key feature entry-points at build time.
 *   2. buildPrefetchMap() derives a URL-path → lazy-import mapping from
 *      the file names (e.g. CalendarPage → /calendar).
 *   3. prefetchRoute() fires the import on touch so the chunk is cached
 *      before the user taps.
 *
 * To add a new prefetchable route you only need to create the page file —
 * no manual map maintenance required.
 */

// Vite resolves these globs at build time into lazy import functions.
// `import: false` means we get () => import(…) functions, not the modules.
const pageModules = import.meta.glob<unknown>(
  '../../pages/**/*Page.{tsx,ts}',
  { import: 'default', eager: false },
);

const featureModules = import.meta.glob<unknown>(
  [
    '../../features/therapist-panel/components/TherapistPanel.{tsx,ts}',
    '../../features/patient-management/PatientManagement.{tsx,ts}',
    '../../features/outcomes/MyOutcomesPage.{tsx,ts}',
    '../../features/work-products/WorkProductTypesPage.{tsx,ts}',
    '../../features/admin-settings/AdminSettings.{tsx,ts}',
  ],
  { import: 'default', eager: false },
);

/**
 * Convert a file path like "../../pages/CalendarPage.tsx" into a URL slug
 * like "/calendar", or "../../pages/billing/BillingHomePage.tsx" into "/billing".
 */
function filePathToSlug(filePath: string): string {
  // Extract the filename without extension
  const match = filePath.match(/\/([^/]+)\.(tsx|ts)$/);
  if (!match) return '';
  const name = match[1];

  // Strip common suffixes
  const base = name
    .replace(/Page$/, '');

  // Convert PascalCase → kebab-case
  return '/' + base
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

/** Well-known aliases that don't follow the filename convention. */
const ALIASES: Record<string, string> = {
  '/new-dashboard': '/dashboard',
  '/note-dashboard': '/notes',
  '/todo-list': '/tasks',
  '/provider-analytics': '/analytics',
  '/my-settings': '/settings',
  '/patient-management': '/cases',
  '/therapist-panel': '/ai-assistant',
};

function buildPrefetchMap(): Record<string, () => Promise<unknown>> {
  const map: Record<string, () => Promise<unknown>> = {};

  const allModules = { ...pageModules, ...featureModules };

  for (const [filePath, importFn] of Object.entries(allModules)) {
    const slug = filePathToSlug(filePath);
    if (!slug || slug === '/') continue;

    // Register under the raw slug
    map[slug] = importFn as () => Promise<unknown>;

    // Register any known alias
    const alias = ALIASES[slug];
    if (alias) {
      map[alias] = importFn as () => Promise<unknown>;
    }
  }

  // Additional alias: /assistant → same as /ai-assistant
  if (map['/ai-assistant']) {
    map['/assistant'] = map['/ai-assistant'];
  }

  return map;
}

const routePrefetchMap = buildPrefetchMap();

const prefetched = new Set<string>();

/**
 * Prefetch the JS chunk for a given route path.
 * No-ops if the route has already been prefetched or has no mapped import.
 */
export function prefetchRoute(path: string): void {
  // Normalize: strip query params and hash
  const cleanPath = path.split('?')[0].split('#')[0];

  if (prefetched.has(cleanPath)) return;

  const importFn = routePrefetchMap[cleanPath];
  if (importFn) {
    prefetched.add(cleanPath);
    // Fire-and-forget — we don't need the module, just want the chunk cached
    importFn().catch(() => {
      // If prefetch fails (e.g. offline), allow retry next time
      prefetched.delete(cleanPath);
    });
  }
}
